import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { MESSAGES } from '../../../constants';
import { selectIsAiEnabled, selectItems } from '../selectors/chat';
import { isAiMessageNodeId } from './useIsAiChatbot';

const AI_MESSAGE_TYPES = [MESSAGES.AI_ARTICLE_ANSWER, MESSAGES.BOT_MESSAGE];

/**
 * Returns boolean indicating whether the current chat is an AI experience or not.
 * For example, the chat will transition from an AI to a non AI experience when confirming a critical flow,
 *  which will be reflected in certain UI elements, such as "bot typing" animation.
 * True is returned for the following cases:
 * - no bot message found yet and it is AI chatbot by specs
 * - first bot message in the last exchange is either AI article answer, or AI start node, or AI bot message node
 */
export const useIsAiExperience = () => {
  const items = useSelector(selectItems);
  const isAiEnabled = useSelector(selectIsAiEnabled);

  return useMemo(() => {
    const lastBotMessage = [...items].reverse().find(({ type }) => AI_MESSAGE_TYPES.includes(type));

    if (!lastBotMessage) {
      return isAiEnabled;
    }

    const userMessages = items.filter(({ type }) => type === MESSAGES.USER_MESSAGE);
    const isBotTyping = items[items.length - 1].type === MESSAGES.BOT_TYPING;

    const lastExchangeUserMessage = isBotTyping
      ? userMessages[userMessages.length - 2]
      : userMessages[userMessages.length - 1];

    const lastExchangeUserMessageIndex = items.indexOf(lastExchangeUserMessage);

    const lastExchangeBotMessage = items
      .slice(lastExchangeUserMessageIndex)
      .find(({ type }) => AI_MESSAGE_TYPES.includes(type));

    return (
      lastExchangeBotMessage &&
      (lastExchangeBotMessage.type === MESSAGES.AI_ARTICLE_ANSWER ||
        ('nodeId' in lastExchangeBotMessage && // Either AI start node or AI bot message
          isAiMessageNodeId(lastExchangeBotMessage.nodeId)))
    );
  }, [items, isAiEnabled]);
};
